'use client';

import { useEffect } from 'react';
import Link from 'next/link';

export default function Error({
    error,
    reset,
}: {
    error: Error & { digest?: string };
    reset: () => void;
}) {
    useEffect(() => {
        console.error('[page error]', error);
    }, [error]);

    // detect locale from the current path without any server APIs
    const locale = typeof window !== 'undefined' && window.location.pathname.startsWith('/ar') ? 'ar' : 'en';
    const isAr   = locale === 'ar';

    return (
        <div
            dir={isAr ? 'rtl' : 'ltr'}
            className="min-h-[calc(100vh-4rem)] flex flex-col items-center justify-center px-4 py-20 text-center"
        >
            <div aria-hidden="true" className="pointer-events-none absolute inset-0 overflow-hidden">
                <div className="absolute top-1/4 inset-x-0 mx-auto w-96 h-96 rounded-full bg-danger-500/6 blur-3xl" />
            </div>

            <div className="relative z-10 max-w-lg w-full space-y-8">
                {/* Icon */}
                <div className="w-20 h-20 rounded-2xl bg-danger-500/10 flex items-center justify-center mx-auto">
                    <svg className="w-10 h-10 text-danger-500" fill="none" stroke="currentColor" strokeWidth={1.5} viewBox="0 0 24 24" aria-hidden="true">
                        <path strokeLinecap="round" strokeLinejoin="round" d="M12 9v3.75m-9.303 3.376c-.866 1.5.217 3.374 1.948 3.374h14.71c1.73 0 2.813-1.874 1.948-3.374L13.949 3.378c-.866-1.5-3.032-1.5-3.898 0L2.697 16.126zM12 15.75h.007v.008H12v-.008z" />
                    </svg>
                </div>

                {/* Message */}
                <div className="space-y-3">
                    <p className="text-xs font-mono text-danger-500 tracking-widest uppercase">
                        {isAr ? 'خطأ في الصفحة' : 'Page Error'}
                    </p>
                    <h1 className="text-2xl sm:text-3xl font-bold text-(--text)">
                        {isAr ? 'حدث خطأ غير متوقع' : 'Something went wrong'}
                    </h1>
                    <p className="text-(--muted) leading-relaxed max-w-sm mx-auto">
                        {isAr
                            ? 'حدث خطأ أثناء تحميل هذه الصفحة. يمكنك المحاولة مجدداً أو العودة إلى الصفحة الرئيسية.'
                            : 'An error occurred while loading this page. Try again or go back home.'}
                    </p>
                </div>

                {/* Actions */}
                <div className="flex flex-col sm:flex-row items-center justify-center gap-3">
                    <button
                        onClick={reset}
                        className="inline-flex items-center gap-2 px-6 py-3 rounded-xl bg-primary-500 text-neutral-900 font-semibold text-sm hover:bg-primary-600 active:bg-primary-700 transition-colors duration-180 cursor-pointer focus-visible:outline-2 focus-visible:outline-primary-500 focus-visible:outline-offset-2"
                    >
                        <svg className="w-4 h-4" fill="none" stroke="currentColor" strokeWidth={2} viewBox="0 0 24 24" aria-hidden="true">
                            <path strokeLinecap="round" strokeLinejoin="round" d="M16.023 9.348h4.992v-.001M2.985 19.644v-4.992m0 0h4.992m-4.993 0l3.181 3.183a8.25 8.25 0 0013.803-3.7M4.031 9.865a8.25 8.25 0 0113.803-3.7l3.181 3.182m0-4.991v4.99" />
                        </svg>
                        {isAr ? 'حاول مجدداً' : 'Try again'}
                    </button>
                    <Link
                        href={`/${locale}`}
                        className="inline-flex items-center gap-2 px-6 py-3 rounded-xl border border-(--border) text-(--text) font-medium text-sm hover:border-primary-500 hover:text-primary-500 transition-colors duration-180 focus-visible:outline-2 focus-visible:outline-primary-500 focus-visible:outline-offset-2"
                    >
                        <svg className="w-4 h-4 rtl:rotate-180" fill="none" stroke="currentColor" strokeWidth={2} viewBox="0 0 24 24" aria-hidden="true">
                            <path strokeLinecap="round" strokeLinejoin="round" d="M10 19l-7-7m0 0l7-7m-7 7h18" />
                        </svg>
                        {isAr ? 'الصفحة الرئيسية' : 'Go home'}
                    </Link>
                </div>

                {process.env.NODE_ENV === 'development' && error.message && (
                    <pre className="mt-4 rounded-xl border border-danger-500/20 bg-danger-500/5 p-4 font-mono text-xs text-start text-danger-400 overflow-auto max-h-40">
                        {error.message}
                        {error.digest ? `\n\ndigest: ${error.digest}` : ''}
                    </pre>
                )}
            </div>
        </div>
    );
}
